<script setup lang="ts">
import { ModalModule } from '@hypercodestudio/basler-components';
import type { LightingAdvisorDecoration } from '~/utils/lightingAdvisor/catalogTypes';
import { resolveLightingAdvisorWarningMessage } from '~/utils/lightingAdvisor/warningMessages';

interface Props {
  modelValue: boolean;
  productName: string | null;
  warnings: LightingAdvisorDecoration[];
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const { $textDictionary } = useNuxtApp();

const open = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value)
});

const resolvedWarnings = computed(() =>
  props.warnings.map((warning) => ({
    identifier: warning.identifier,
    message: resolveLightingAdvisorWarningMessage(
      warning.identifier,
      ($textDictionary.value ?? {}) as Record<string, string | undefined>
    )
  }))
);
</script>

<template>
  <ModalModule
    v-model="open"
    :title="
      productName
        ? (
            $textDictionary['lightingAdvisor.warning.modal.title'] ??
            'Notes for {productName}'
          ).replace('{productName}', productName)
        : ($textDictionary['lightingAdvisor.warning.modal.titleFallback'] ??
          'Important notes')
    "
    close-icon-text="close"
    :esc-to-close="true"
    :click-to-close="true"
    data-testid="lighting-advisor-warnings-modal"
    @abort="open = false"
  >
    <div
      v-for="(warning, index) in resolvedWarnings"
      :key="warning.identifier"
      class="lighting-advisor-warnings-modal__item"
    >
      <p>
        <strong>
          {{ $textDictionary['lightingAdvisor.warning.modal.note'] ?? 'Note' }}
          {{ index + 1 }}
        </strong>
      </p>
      <div class="rte-text" v-html="warning.message" />
    </div>
  </ModalModule>
</template>

<style scoped>
.lighting-advisor-warnings-modal__item {
  margin-bottom: 1rem;
}

.lighting-advisor-warnings-modal__item:last-child {
  margin-bottom: 0;
}
</style>
